import { useSortable, SortableTh } from "@/components/common/SortableTable";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API_BASE, fmtCurrency, fmtNumber } from "@/lib/api";
import { GBELogo } from "@/components/layout/GBELogo";
import { Button } from "@/components/ui/button";
import { Printer, ShieldAlert, Clock, Loader2, FileDown } from "lucide-react";
import { TID } from "@/constants/testIds";

// Public axios (no bearer/credentials)
const pub = axios.create({ baseURL: API_BASE, timeout: 20000 });

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return iso; }
}

export default function ClientPortalPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const { sorted: sortedPortalAssets, sort: cpSort, setSort: setCpSort } = useSortable(data?.report?.top_immediate || []);

  useEffect(() => {
    pub.get(`/capital/public/board-report/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => {
        const status = e.response?.status;
        setError(
          status === 410 ? "This share link has been revoked or has expired."
          : status === 404 ? "This share link is not valid."
          : "Could not load the report. Please contact GBE Group."
        );
      })
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-gbe animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <div className="w-14 h-14 rounded-full bg-rose-50 border border-rose-200 flex items-center justify-center mx-auto mb-4">
            <ShieldAlert className="w-6 h-6 text-rose-700" />
          </div>
          <h1 className="font-display text-2xl font-semibold text-slate-900">Access unavailable</h1>
          <p className="text-slate-600 mt-2">{error}</p>
          <p className="text-xs text-slate-400 mt-4">
            If you believe this is an error, please contact your GBE Group account manager for a fresh link.
          </p>
        </div>
      </div>
    );
  }

  const { client, label, expires_at, report } = data;
  const ov = report.overview;
  const imm = ov.horizons.find((h) => h.horizon === "Immediate Attention") || { count: 0, budget: 0 };
  const m12 = ov.horizons.find((h) => h.horizon === "12 Month Plan") || { count: 0, budget: 0 };
  const m24 = ov.horizons.find((h) => h.horizon === "24 Month Plan") || { count: 0, budget: 0 };
  const totalRenewal = imm.budget + m12.budget + m24.budget;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header bar */}
      <header className="bg-gbe text-white print:hidden">
        <div className="max-w-[1100px] mx-auto px-6 py-4 flex items-center justify-between">
          <GBELogo />
          <div className="flex items-center gap-4">
            <div className="hidden sm:block text-right">
              <div className="text-[10px] uppercase tracking-widest text-slate-400">Prepared for</div>
              <div className="text-sm text-white truncate max-w-[280px]">{client}</div>
            </div>
            <Button
              data-testid="client-portal-download-pdf"
              onClick={() => { window.location.href = `${API_BASE}/capital/public/board-report/${token}.pdf`; }}
              variant="outline"
              className="h-9 border-white/30 text-white bg-white/10 hover:bg-white/20"
            >
              <FileDown className="w-4 h-4 mr-1.5" /> Branded PDF
            </Button>
            <Button
              data-testid={TID.clientPortalPrint}
              onClick={() => window.print()}
              className="bg-gbe-accent hover:bg-gbe-accent-hover text-gbe text-white h-9"
            >
              <Printer className="w-4 h-4 mr-1.5" /> Print
            </Button>
          </div>
        </div>
      </header>

      {/* Shared-link notice */}
      <div className="bg-blue-50 border-b border-blue-100 text-gbe text-xs print:hidden">
        <div className="max-w-[1100px] mx-auto px-6 py-2 flex flex-wrap items-center gap-3">
          <Clock className="w-3.5 h-3.5" />
          <span>You're viewing a secure link shared by GBE Group{label ? ` — ${label}` : ""}. Access expires {formatDate(expires_at)}.</span>
        </div>
      </div>

      <main className="max-w-[1100px] mx-auto px-6 py-8">
        <div className="bg-white border border-slate-200 rounded-lg p-8 print:border-0 print:shadow-none print:p-0">
          <div className="border-b border-slate-200 pb-6 mb-6">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Asset Intelligence</div>
            <h1 className="font-display text-3xl font-semibold text-slate-900 mt-1">Strategic Renewal Assessment</h1>
            <p className="text-sm text-slate-600 mt-1">Prepared for <span className="font-medium text-slate-900">{client}</span> by GBE Group</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
            <Stat label="Assets Reviewed" value={fmtNumber(ov.asset_count)} />
            <Stat label="Portfolio Replacement Value" value={fmtCurrency(ov.total_value)} />
            <Stat label="Avg Health Score" value={`${ov.avg_health.toFixed(1)} / 100`} />
            <Stat label="Data Confidence" value={`${ov.avg_confidence.toFixed(1)} / 100`} />
          </div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <Horizon title="Beyond Design Life" tone="rose" count={imm.count} budget={imm.budget} />
            <Horizon title="12 Month Plan" tone="amber" count={m12.count} budget={m12.budget} />
            <Horizon title="24 Month Plan" tone="blue" count={m24.count} budget={m24.budget} />
          </div>

          <div className="mt-6 rounded-md border border-blue-200 bg-blue-50/40 p-5">
            <div className="text-[10px] uppercase tracking-widest text-gbe font-medium">Recommended annual capital allocation</div>
            <div className="mt-1 font-display text-3xl font-semibold text-slate-900 tabular-nums">{fmtCurrency(imm.budget + m12.budget)}</div>
            <div className="text-sm text-slate-600 mt-1">
              Combining Beyond Design Life + 12 Month Plan. Total 24-month strategic outlay: <span className="font-medium text-slate-900">{fmtCurrency(totalRenewal)}</span>.
            </div>
          </div>

          <section className="mt-8">
            <h2 className="font-display text-xl font-semibold text-slate-900 mb-3">Top 10 Immediate-Attention Assets</h2>
            <div className="overflow-x-auto border border-slate-200 rounded-md">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left font-medium px-4 py-2">#</th>
                    <SortableTh label="Asset" k="asset_name" sort={cpSort} setSort={setCpSort} className="text-left font-medium px-4 py-2" />
                    <SortableTh label="Site" k="site_name" sort={cpSort} setSort={setCpSort} className="text-left font-medium px-4 py-2" />
                    <SortableTh label="Class" k="asset_group" sort={cpSort} setSort={setCpSort} className="text-left font-medium px-4 py-2" />
                    <SortableTh label="Condition" k="condition" sort={cpSort} setSort={setCpSort} className="text-right font-medium px-4 py-2" />
                    <SortableTh label="Priority" k="v6_priority_score" sort={cpSort} setSort={setCpSort} className="text-right font-medium px-4 py-2" />
                    <SortableTh label="Cost" k="cost" sort={cpSort} setSort={setCpSort} className="text-right font-medium px-4 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {sortedPortalAssets.map((a, i) => (
                    <tr key={a.asset_id} className="border-b border-slate-100">
                      <td className="px-4 py-2 text-slate-500 tabular-nums">{i + 1}</td>
                      <td className="px-4 py-2 text-slate-800 max-w-[280px] truncate">{a.asset_name}</td>
                      <td className="px-4 py-2 text-slate-600 max-w-[180px] truncate">{a.site_name}</td>
                      <td className="px-4 py-2 text-slate-600">{a.asset_group}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{a.condition ?? "—"}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{Math.round(a.v6_priority_score ?? 0)}</td>
                      <td className="px-4 py-2 text-right tabular-nums font-medium">{fmtCurrency(a.cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-display text-lg font-semibold text-slate-900 mb-2">Top Sites</h3>
              <ul>
                {report.top_sites.slice(0, 8).map((s) => (
                  <li key={s.site} className="flex items-center justify-between border-b border-slate-100 py-2 text-sm">
                    <span className="text-slate-800 truncate mr-4">{s.site}</span>
                    <span className="text-slate-900 font-medium tabular-nums">{fmtCurrency(s.total_renewal_budget)}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="font-display text-lg font-semibold text-slate-900 mb-2">Top Asset Classes</h3>
              <ul>
                {report.top_asset_classes.slice(0, 8).map((g) => (
                  <li key={g.asset_group} className="flex items-center justify-between border-b border-slate-100 py-2 text-sm">
                    <span className="text-slate-800">{g.asset_group}</span>
                    <span className="text-slate-900 font-medium tabular-nums">{fmtCurrency(g.total_renewal_budget)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <section className="mt-8">
            <h3 className="font-display text-lg font-semibold text-slate-900 mb-3">Investment Scenarios</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {report.scenarios.map((s) => (
                <div key={s.scenario} className="border border-slate-200 rounded-md p-4">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">{s.purpose}</div>
                  <div className="font-display text-lg font-semibold text-slate-900 mt-1">{s.scenario}</div>
                  <div className="mt-2 font-display text-2xl font-semibold text-slate-900 tabular-nums">{fmtCurrency(s.budget)}</div>
                  <div className="text-xs text-slate-500">{s.included_assets} assets</div>
                </div>
              ))}
            </div>
          </section>

          <footer className="mt-10 pt-6 border-t border-slate-200 text-xs text-slate-500 space-y-1">
            <p>Assets shown here are identified for renewal assessment, not final replacement approval.</p>
            <p>Source: AroFlo Asset Intelligence · Prepared by GBE Group.</p>
          </footer>
        </div>

        <div className="text-center text-xs text-slate-400 mt-6 print:hidden">
          This link was shared by GBE Group and is confidential. Do not forward without permission.
        </div>
      </main>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-slate-200 rounded-md p-4">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">{label}</div>
      <div className="mt-1 font-display text-xl font-semibold text-slate-900 tabular-nums">{value}</div>
    </div>
  );
}

function Horizon({ title, tone, count, budget }) {
  const t = tone === "rose" ? "border-rose-200 bg-rose-50/40" : tone === "amber" ? "border-amber-200 bg-amber-50/40" : "border-blue-200 bg-blue-50/40";
  const c = tone === "rose" ? "text-rose-700" : tone === "amber" ? "text-amber-700" : "text-gbe";
  return (
    <div className={`rounded-md border ${t} p-4`}>
      <div className={`text-[10px] uppercase tracking-widest font-medium ${c}`}>{title}</div>
      <div className="mt-1 font-display text-2xl font-semibold text-slate-900 tabular-nums">{fmtCurrency(budget)}</div>
      <div className="text-xs text-slate-500 mt-0.5">{count} assets</div>
    </div>
  );
}
